import { InputAdornment, Stack } from '@mui/material';

import { OpenInNewTabButton, RemoveButton, SectionHeading } from 'ContentItemEditor';
import { useFieldArray, useFormContext } from 'FrontRoyalReactHookForm';
import { TextField } from 'FrontRoyalMaterialUiForm';
import AssetUrlText from './AssetUrlText';
import FileTypeIcon from './FileTypeIcon';
import ItemPanel from './ItemPanel';
import ReorderButtons from './ReorderButtons';
import UploadFilesButton from './UploadFilesButton';
import { type StreamFormValues } from './StreamEditor.types';

type Props = {
    uploading: boolean;
    onPickFiles: (files: File[]) => void;
};

// Files an admin uploads for a course, listed on the course dashboard under Additional Resources.
export default function ResourceDownloadsSection({ uploading, onPickFiles }: Props) {
    const { control, watch } = useFormContext<StreamFormValues>();
    const { fields, remove, move } = useFieldArray({ control, name: 'resource_downloads' });
    const downloads = watch('resource_downloads');

    return (
        <Stack spacing={2}>
            <SectionHeading>Downloads</SectionHeading>

            {fields.map((field, index) => (
                <ItemPanel key={field.id}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <AssetUrlText url={downloads?.[index]?.url || ''} />

                        <Stack direction="row" sx={{ alignItems: 'center' }}>
                            {!!downloads?.[index]?.url && (
                                <OpenInNewTabButton href={downloads[index].url} label="Test this link" />
                            )}

                            <ReorderButtons index={index} count={fields.length} label="this download" onMove={move} />

                            <RemoveButton label="Remove this download" onClick={() => remove(index)} />
                        </Stack>
                    </Stack>

                    <Stack direction="row" spacing={1}>
                        <TextField
                            name={`resource_downloads.${index}.title`}
                            label="Title for display"
                            fullWidth
                            slotProps={{ htmlInput: { dir: 'auto' } }}
                        />

                        <TextField
                            name={`resource_downloads.${index}.type`}
                            label="Filetype"
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FileTypeIcon type={downloads?.[index]?.type} />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Stack>
                </ItemPanel>
            ))}

            <UploadFilesButton uploading={uploading} onPick={onPickFiles} />
        </Stack>
    );
}
